import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DollarSign, TrendingUp, Users, Link2, Copy, CheckCircle,
  BarChart3, Clock, ArrowRight, ExternalLink, Repeat,
  Award, Zap, ChevronRight, Plus, Eye, MousePointer,
  UserPlus, CreditCard, RefreshCw, Trophy, Building2, ChevronDown, Loader2, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFERRAL_BASE = 'https://www.americanrea.com';

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [links, setLinks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [referredMembers, setReferredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedLink, setCopiedLink] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bankingOpen, setBankingOpen] = useState(false);
  const [banking, setBanking] = useState(null);
  const [bankForm, setBankForm] = useState({ bank_name: '', account_holder: '', routing_number: '', account_number: '', account_type: 'checking' });
  const [bankSaving, setBankSaving] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Link builder state
  const [newCampaign, setNewCampaign] = useState('');
  const [newSource, setNewSource] = useState('');
  const [newDest, setNewDest] = useState('/register');

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [sumRes, linksRes, actRes, memRes, lbRes, bankRes, payHistRes, analyticsRes] = await Promise.all([
          fetch(`${API}/affiliate/partner/summary`, { headers: headers() }),
          fetch(`${API}/affiliate/partner/links`, { headers: headers() }),
          fetch(`${API}/affiliate/partner/activity?limit=10`, { headers: headers() }),
          fetch(`${API}/affiliate/partner/referred-members`, { headers: headers() }),
          fetch(`${API}/affiliate/partner/leaderboard`, { headers: headers() }),
          fetch(`${API}/affiliate/partner/banking`, { headers: headers() }),
          fetch(`${API}/affiliate/partner/payouts`, { headers: headers() }),
          fetch(`${API}/affiliate/partner/analytics`, { headers: headers() }),
        ]);
        if (sumRes.ok) setSummary(await sumRes.json());
        if (linksRes.ok) { const d = await linksRes.json(); setLinks(d.links || []); }
        if (actRes.ok) { const d = await actRes.json(); setActivity(d.activity || []); }
        if (memRes.ok) { const d = await memRes.json(); setReferredMembers(d.members || []); }
        if (lbRes.ok) { const d = await lbRes.json(); setLeaderboard(d.leaderboard || []); }
        if (bankRes.ok) { const d = await bankRes.json(); setBanking(d); }
        if (payHistRes.ok) { const d = await payHistRes.json(); setPayoutHistory(d.payouts || []); }
        if (analyticsRes.ok) { const d = await analyticsRes.json(); setAnalytics(d); }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [headers]);

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${REFERRAL_BASE}/register?ref=${code}`);
    setCopiedLink(code);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const createLink = async () => {
    if (!newCampaign.trim()) { toast.error('Campaign name required'); return; }
    const res = await fetch(`${API}/affiliate/partner/create-link`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ campaign_name: newCampaign, source_tag: newSource, destination_url: newDest }),
    });
    if (res.ok) {
      const d = await res.json();
      setLinks(prev => [d.link, ...prev]);
      setNewCampaign(''); setNewSource('');
      toast.success('Link created!');
    }
  };

  const fmt = (v) => {
    if (v === null || v === undefined) return '$0.00';
    return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-28 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}
        </div>
      </div>
    );
  }

  if (!summary) return <div className="p-8 text-gray-500">Unable to load partner dashboard.</div>;

  const metrics = [
    { label: "Lifetime Earnings", value: fmt(summary.total_earned), icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Monthly Recurring", value: fmt(summary.monthly_recurring_earnings), icon: Repeat, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Active Referred", value: summary.active_referred_members, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Total Clicks", value: summary.total_clicks, icon: MousePointer, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Pending Balance", value: fmt(summary.pending_balance), icon: Clock, color: "text-orange-600", bg: "bg-orange-50" },
    { label: "Approved Balance", value: fmt(summary.approved_balance), icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
    { label: "Total Paid", value: fmt(summary.total_paid), icon: CreditCard, color: "text-indigo-600", bg: "bg-indigo-50" },
    { label: "Commission Rate", value: `${summary.commission_rate}%`, icon: TrendingUp, color: "text-pink-600", bg: "bg-pink-50" },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'links', label: 'Links' },
    { id: 'referrals', label: 'Referrals' },
    { id: 'activity', label: 'Activity' },
  ];

  const onboardingSteps = [
    { label: "Accept terms", done: summary.terms_accepted },
    { label: "Add bank account", done: summary.stripe_onboarding_complete },
    { label: "Submit tax info", done: summary.tax_info_complete },
    { label: "Create first link", done: links.length > 1 },
    { label: "Share first campaign", done: summary.total_clicks > 0 },
  ];
  const onboardingComplete = onboardingSteps.filter(s => s.done).length;

  return (
    <div className="max-w-7xl mx-auto" data-testid="partner-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0f1f33]">Partner Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Track referrals, recurring commissions, and payouts.</p>
      </div>

      {/* Referral Link Banner */}
      <div className="mb-6 bg-[#f0f4ff] rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-blue-100">
        <div>
          <p className="text-sm font-semibold text-[#1e3a5f] mb-1">Your Referral Link</p>
          <p className="text-sm text-gray-500 font-mono break-all">{REFERRAL_BASE}/register?ref={summary.referral_code}</p>
        </div>
        <Button onClick={() => copyLink(summary.referral_code)} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white flex-shrink-0" data-testid="copy-referral-link">
          {copiedLink === summary.referral_code ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copiedLink === summary.referral_code ? 'Copied!' : 'Copy Link'}
        </Button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {metrics.map((m, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm" data-testid={`metric-${m.label.toLowerCase().replace(/\s/g,'-')}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-8 h-8 rounded-lg ${m.bg} flex items-center justify-center`}>
                <m.icon className={`w-4 h-4 ${m.color}`} />
              </div>
              <span className="text-xs text-gray-400 font-medium">{m.label}</span>
            </div>
            <p className="text-xl font-bold text-[#0f1f33]">{m.value}</p>
          </div>
        ))}
      </div>

      {/* Banking Details Section */}
      <div className="mb-8" data-testid="banking-section">
        <button onClick={() => setBankingOpen(!bankingOpen)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-gray-200 transition-all">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${banking?.has_banking ? 'bg-green-50' : 'bg-amber-50'}`}>
              <Building2 className={`w-4.5 h-4.5 ${banking?.has_banking ? 'text-green-600' : 'text-amber-600'}`} />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-[#0f1f33]">Banking Details</p>
              <p className="text-xs text-gray-400">
                {banking?.has_banking
                  ? `${banking.bank_name} ••••${banking.account_number_last4} — ${banking.account_type}`
                  : 'Add your bank account for direct deposit payouts'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {banking?.has_banking
              ? <Badge className="bg-green-50 text-green-700 text-xs">Verified</Badge>
              : <Badge className="bg-amber-50 text-amber-700 text-xs">Required</Badge>}
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${bankingOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {bankingOpen && (
          <div className="mt-2 p-5 bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="banking-form">
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Bank Name</label>
                <Input value={bankForm.bank_name} onChange={e => setBankForm(p => ({ ...p, bank_name: e.target.value }))}
                  placeholder="e.g., Chase Bank" data-testid="bank-name-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Account Holder Name</label>
                <Input value={bankForm.account_holder} onChange={e => setBankForm(p => ({ ...p, account_holder: e.target.value }))}
                  placeholder="Full legal name" data-testid="account-holder-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Routing Number (9 digits)</label>
                <Input value={bankForm.routing_number} onChange={e => setBankForm(p => ({ ...p, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                  placeholder="123456789" maxLength={9} data-testid="routing-number-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Account Number</label>
                <Input value={bankForm.account_number} onChange={e => setBankForm(p => ({ ...p, account_number: e.target.value.replace(/\D/g, '').slice(0, 17) }))}
                  placeholder="••••••••1234" maxLength={17} data-testid="account-number-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <label className="text-xs text-gray-500">Account Type:</label>
                <select value={bankForm.account_type} onChange={e => setBankForm(p => ({ ...p, account_type: e.target.value }))}
                  className="h-9 rounded-md border border-gray-200 px-3 text-sm" data-testid="account-type-select">
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </div>
              <Button onClick={async () => {
                setBankSaving(true);
                try {
                  const res = await fetch(`${API}/affiliate/partner/banking`, {
                    method: 'PUT', headers: headers(),
                    body: JSON.stringify(bankForm),
                  });
                  if (res.ok) {
                    const d = await res.json();
                    setBanking({ has_banking: true, ...bankForm, account_number_last4: d.account_number_last4 });
                    setBankForm(p => ({ ...p, account_number: '' }));
                    toast.success('Banking details saved');
                  } else {
                    const err = await res.json();
                    toast.error(err.detail || 'Failed to save');
                  }
                } catch { toast.error('Error saving banking details'); }
                setBankSaving(false);
              }}
                disabled={bankSaving || !bankForm.bank_name || !bankForm.account_holder || !bankForm.routing_number || !bankForm.account_number}
                className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-banking-btn">
                {bankSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Save Banking Details
              </Button>
            </div>
            {banking?.has_banking && banking?.updated_at && (
              <p className="text-xs text-gray-400 mt-3">Last updated: {banking.updated_at.slice(0, 10)}</p>
            )}

            {/* Payout History */}
            <div className="mt-5 pt-5 border-t border-gray-100" data-testid="payout-history">
              <h4 className="text-sm font-semibold text-[#0f1f33] mb-3">Payout History</h4>
              {payoutHistory.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">No payouts yet. Commissions that meet the threshold will appear here.</p>
              ) : (
                <div className="space-y-2">
                  {payoutHistory.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`payout-row-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${p.status === 'paid' ? 'bg-green-100' : p.status === 'approved' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                          {p.status === 'paid' ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : p.status === 'approved' ? <CreditCard className="w-3.5 h-3.5 text-blue-600" /> : <Clock className="w-3.5 h-3.5 text-amber-600" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#0f1f33] capitalize">{p.status}</p>
                          <p className="text-xs text-gray-400">
                            {p.created_at?.slice(0, 10)}
                            {p.eta_date && ` · ETA: ${p.eta_date}`}
                            {p.processed_at && ` · Paid: ${p.processed_at.slice(0, 10)}`}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-emerald-600">{fmt(p.amount)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-50 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === t.id ? 'bg-white text-[#0f1f33] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Onboarding Checklist */}
          <Card className="border-gray-100 shadow-sm lg:col-span-1">
            <CardContent className="p-6">
              <h3 className="font-semibold text-[#0f1f33] mb-1">Onboarding</h3>
              <p className="text-xs text-gray-400 mb-4">{onboardingComplete}/5 complete</p>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-5">
                <div className="bg-[#2563eb] rounded-full h-2 transition-all" style={{ width: `${(onboardingComplete / 5) * 100}%` }} />
              </div>
              <div className="space-y-3">
                {onboardingSteps.map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${s.done ? 'bg-[#2563eb] border-[#2563eb]' : 'border-gray-200'}`}>
                      {s.done && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                    <span className={`text-sm ${s.done ? 'text-gray-400 line-through' : 'text-[#0f1f33]'}`}>{s.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Funnel Summary */}
          <Card className="border-gray-100 shadow-sm lg:col-span-2">
            <CardContent className="p-6">
              <h3 className="font-semibold text-[#0f1f33] mb-4">Referral Funnel</h3>
              <div className="grid grid-cols-5 gap-3">
                {[
                  { label: "Clicks", value: summary.total_clicks, icon: MousePointer },
                  { label: "Leads", value: summary.total_leads, icon: UserPlus },
                  { label: "Conversions", value: summary.total_conversions, icon: Zap },
                  { label: "Pending", value: summary.pending_conversions, icon: Clock },
                  { label: "Approved", value: summary.approved_conversions, icon: CheckCircle },
                ].map((f, i) => (
                  <div key={i} className="text-center p-3 bg-gray-50 rounded-xl">
                    <f.icon className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <p className="text-lg font-bold text-[#0f1f33]">{f.value}</p>
                    <p className="text-xs text-gray-400">{f.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top 5 Leaderboard */}
          {leaderboard.length > 0 && (
            <Card className="border-gray-100 shadow-sm lg:col-span-3" data-testid="leaderboard-widget">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <h3 className="font-semibold text-[#0f1f33]">Top 5 Partners</h3>
                </div>
                <div className="space-y-3">
                  {leaderboard.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl" data-testid={`leaderboard-rank-${p.rank}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-200 text-gray-700' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>
                          {p.rank}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#0f1f33]">{p.name}</p>
                          <p className="text-xs text-gray-400 capitalize">{p.partner_type?.replace('_', ' ')} &middot; {p.tier}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-600">{fmt(p.total_earned)}</p>
                        <p className="text-xs text-gray-400">{p.total_conversions} conv &middot; {p.active_referred_members} active</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6" data-testid="analytics-tab">
          {analytics ? (
            <>
              {/* Export Button */}
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => {
                  const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
                  fetch(`${API}/affiliate/partner/export-csv`, { headers: { 'Authorization': `Bearer ${token}` } })
                    .then(r => r.blob()).then(blob => {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `area_partner_earnings.csv`; a.click(); URL.revokeObjectURL(url);
                      toast.success('Earnings report downloaded');
                    }).catch(() => toast.error('Failed to download'));
                }} data-testid="export-partner-csv">
                  <Download className="w-4 h-4 mr-2" /> Export Earnings CSV
                </Button>
              </div>
              {/* Monthly Earnings Chart */}
              <Card className="border-gray-100 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#0f1f33] mb-1">Monthly Earnings</h3>
                  <p className="text-xs text-gray-400 mb-5">Last 6 months of commission earnings</p>
                  <div className="flex items-end gap-3 h-44" data-testid="earnings-chart">
                    {analytics.monthly_earnings.map((m, i) => {
                      const maxEarnings = Math.max(...analytics.monthly_earnings.map(x => x.earnings), 1);
                      const height = Math.max((m.earnings / maxEarnings) * 100, 4);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <p className="text-xs font-semibold text-emerald-600">{m.earnings > 0 ? fmt(m.earnings) : ''}</p>
                          <div className="w-full rounded-t-lg bg-gradient-to-t from-[#2563eb] to-[#60a5fa] transition-all duration-500" style={{ height: `${height}%` }} data-testid={`bar-${i}`} />
                          <p className="text-xs text-gray-400 mt-1">{m.month_short}</p>
                          <p className="text-xs text-gray-300">{m.conversions} conv</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Funnel Conversion Rates */}
              <Card className="border-gray-100 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#0f1f33] mb-1">Conversion Funnel</h3>
                  <p className="text-xs text-gray-400 mb-5">Click-to-conversion performance (all time)</p>
                  <div className="space-y-4" data-testid="funnel-rates">
                    {[
                      { label: "Clicks", value: analytics.funnel.clicks, width: 100, color: "bg-blue-500" },
                      { label: "Leads", value: analytics.funnel.leads, width: analytics.funnel.clicks > 0 ? (analytics.funnel.leads / analytics.funnel.clicks * 100) : 0, color: "bg-violet-500", rate: `${analytics.funnel.click_to_lead}%` },
                      { label: "Conversions", value: analytics.funnel.conversions, width: analytics.funnel.clicks > 0 ? (analytics.funnel.conversions / analytics.funnel.clicks * 100) : 0, color: "bg-emerald-500", rate: `${analytics.funnel.lead_to_conv}%` },
                      { label: "Approved", value: analytics.funnel.approved, width: analytics.funnel.clicks > 0 ? (analytics.funnel.approved / analytics.funnel.clicks * 100) : 0, color: "bg-green-600", rate: `${analytics.funnel.approval_rate}%` },
                    ].map((step, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#0f1f33]">{step.label}</span>
                            <span className="text-sm text-gray-400">{step.value}</span>
                          </div>
                          {step.rate && <span className="text-xs font-semibold text-[#2563eb]">{step.rate}</span>}
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-3">
                          <div className={`${step.color} rounded-full h-3 transition-all duration-700`} style={{ width: `${Math.max(step.width, 2)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#2563eb]">{analytics.funnel.click_to_conv}%</p>
                      <p className="text-xs text-gray-400">Click → Conversion</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{analytics.funnel.approval_rate}%</p>
                      <p className="text-xs text-gray-400">Approval Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Event Type Breakdown */}
                <Card className="border-gray-100 shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-[#0f1f33] mb-4">Revenue by Event Type</h3>
                    <div className="space-y-3" data-testid="event-breakdown">
                      {Object.entries(analytics.event_breakdown).length === 0 ? (
                        <p className="text-sm text-gray-400 py-4 text-center">No conversion events yet.</p>
                      ) : (
                        Object.entries(analytics.event_breakdown).map(([type, data], i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${type === 'first_purchase' ? 'bg-emerald-50' : type === 'renewal' ? 'bg-blue-50' : 'bg-violet-50'}`}>
                                {type === 'first_purchase' ? <Zap className="w-4 h-4 text-emerald-600" /> : type === 'renewal' ? <Repeat className="w-4 h-4 text-blue-600" /> : <TrendingUp className="w-4 h-4 text-violet-600" />}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#0f1f33] capitalize">{type.replace('_', ' ')}</p>
                                <p className="text-xs text-gray-400">{data.count} events</p>
                              </div>
                            </div>
                            <p className="text-sm font-semibold text-emerald-600">{fmt(data.total)}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Performing Links */}
                <Card className="border-gray-100 shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-[#0f1f33] mb-4">Top Performing Links</h3>
                    <div className="space-y-3" data-testid="top-links">
                      {analytics.top_links.length === 0 ? (
                        <p className="text-sm text-gray-400 py-4 text-center">No link clicks yet.</p>
                      ) : (
                        analytics.top_links.map((l, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                <span className="text-xs font-bold text-blue-600">#{i + 1}</span>
                              </div>
                              <p className="text-sm font-mono text-[#0f1f33]">{l.code}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <MousePointer className="w-3.5 h-3.5 text-gray-400" />
                              <p className="text-sm font-semibold text-[#0f1f33]">{l.clicks} clicks</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}</div>
          )}
        </div>
      )}

      {activeTab === 'links' && (
        <div className="space-y-6">
          {/* Link Builder */}
          <Card className="border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold text-[#0f1f33] mb-4">Create New Link</h3>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Campaign Name</label>
                  <Input value={newCampaign} onChange={e => setNewCampaign(e.target.value)} placeholder="e.g., Instagram Bio" data-testid="link-campaign-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Source Tag</label>
                  <Input value={newSource} onChange={e => setNewSource(e.target.value)} placeholder="e.g., instagram" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Destination</label>
                  <select value={newDest} onChange={e => setNewDest(e.target.value)} className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm">
                    <option value="/register">Registration Page</option>
                    <option value="/membership">Membership Page</option>
                    <option value="/affiliate">Affiliate Page</option>
                    <option value="/">Homepage</option>
                  </select>
                </div>
              </div>
              <Button onClick={createLink} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="create-link-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Link
              </Button>
            </CardContent>
          </Card>

          {/* Links List */}
          <Card className="border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold text-[#0f1f33] mb-4">Your Links</h3>
              {links.length === 0 ? (
                <p className="text-gray-400 text-sm">No links yet. Create your first campaign link above.</p>
              ) : (
                <div className="space-y-3">
                  {links.map((l, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Link2 className="w-4 h-4 text-[#2563eb]" />
                          <span className="text-sm font-medium text-[#0f1f33]">{l.campaign_name || 'Default'}</span>
                          {l.source_tag && <Badge variant="secondary" className="text-xs">{l.source_tag}</Badge>}
                        </div>
                        <p className="text-xs text-gray-400 font-mono truncate">{REFERRAL_BASE}{l.destination_url}?ref={l.code}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyLink(l.code)} className="flex-shrink-0">
                        {copiedLink === l.code ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'referrals' && (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-[#0f1f33] mb-4">Referred Members</h3>
            {referredMembers.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No referred members yet. Share your link to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Member</th>
                    <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Tier</th>
                    <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Status</th>
                    <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Locked</th>
                    <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Referred</th>
                  </tr></thead>
                  <tbody>
                    {referredMembers.map((m, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-3 px-2 font-medium text-[#0f1f33]">{m.name}</td>
                        <td className="py-3 px-2"><Badge variant="outline" className="capitalize">{m.tier}</Badge></td>
                        <td className="py-3 px-2"><Badge className={`text-xs ${m.status === 'converted' ? 'bg-green-50 text-green-700' : m.status === 'qualified' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'}`}>{m.status}</Badge></td>
                        <td className="py-3 px-2">{m.attribution_locked ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-gray-300" />}</td>
                        <td className="py-3 px-2 text-gray-400">{m.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'activity' && (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-[#0f1f33] mb-4">Recent Commission Activity</h3>
            {activity.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No commission activity yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activity.map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.type === 'renewal' ? 'bg-blue-50' : a.type === 'upgrade' ? 'bg-violet-50' : 'bg-emerald-50'}`}>
                        {a.type === 'renewal' ? <RefreshCw className="w-4 h-4 text-blue-600" /> : a.type === 'upgrade' ? <TrendingUp className="w-4 h-4 text-violet-600" /> : <Zap className="w-4 h-4 text-emerald-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0f1f33]">{a.member_name}</p>
                        <p className="text-xs text-gray-400 capitalize">{a.type.replace('_', ' ')} &middot; {fmt(a.amount)} sale</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-600">+{fmt(a.commission)}</p>
                      <Badge className={`text-xs ${a.status === 'paid' ? 'bg-green-50 text-green-700' : a.status === 'approved' ? 'bg-blue-50 text-blue-700' : a.status === 'reversed' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'}`}>{a.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
